import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from '../helpers/CommonHelpers';
import { autoComplete } from '../redux/slices/searchActions';
import SearchSuggestions from '../components/SearchSuggestions';
import { buildURL, getDefaultSort } from '../utils/searchFunctions';
import { setOptions, setAutocompleteResults, setIsClearFilter } from '../redux/slices/search.slice';

export default function Search() {
    const dispatch = useDispatch();
    const [searchQuery, setSearchQuery] = useState('');
    const searchOptions = useSelector((state) => state.search.searchOptions);
    const [searchParams, setSearchParams] = useState(new URLSearchParams());
    const searchFilters = useSelector((state) => state.search.searchFilters);
    const [clearSearchQuery, setClearSearchQuery] = useState(false);
    const isClearFilter = useSelector((state) => state.search.isClearFilter);
    const [showSearchSuggestion, setShowSearchSuggestion] = useState(false);

    const filterSearchRef = useRef(null);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        // Support both 'q' (React search) and 's' (WordPress default) parameters
        setSearchQuery(searchParams.get('q') || searchParams.get('s') || '');
        setSearchParams(searchParams);
    }, []);

    useEffect(() => {
        if (isClearFilter) {
            setSearchQuery('');
            dispatch(setIsClearFilter(false));
        }
    }, [isClearFilter]);

    useEffect(() => {
        if (searchFilters && searchFilters.subjectArea) {
            getAutocompleteResults(searchQuery);
        }
    }, [searchFilters]);

    const searchContent = async (_searchOptions) => {
        let options = {
            ..._searchOptions,
            licensing_Tech: null,
            products: null,
            cloud_OnPremises_Hybrid: null,
            contentType: null,
            mediaType: null,
            subjectArea: null,
            datePublishedPreset: '',
            datePublishedStart: '',
            datePublishedEnd: '',
            authors: null,
            archived: false,
            skip: 0,
            take: 8,
            total: 8,
            overrideReplacement: false,
            expandSearch: false,
            score: searchParams.get('score') || false,
            popularity: searchParams.get('popularity') || true,
        };

        let defaultSort = getDefaultSort(options);
        options = { ...options, sortBy: defaultSort };

        dispatch(setOptions(options));
        const urlOptions = buildURL(options);
        if (window)
            window.location.assign(
                _searchOptions?.isPublicSearch
                    ? `/search?${urlOptions}`
                    : `/atlas/search?${urlOptions}`
            );
    };

    const getAutocompleteResults = (query) => {
        const autocompleteOptions = {
            searchQuery: query,
        };

        let autocompleteResults = {};
        if (query !== '') {
            dispatch(autoComplete(autocompleteOptions));
        } else {
            dispatch(setAutocompleteResults(autocompleteResults));
        }
    };

    const handleMouseDown = (e) => {
        if (filterSearchRef.current && !filterSearchRef.current.contains(e.target)) {
            setShowSearchSuggestion(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, []);

    return (
        <>
            <div className="search-wrapper" ref={filterSearchRef}>
                <div className="searchoption">
                    <input
                        className="nav-search"
                        type="text"
                        autocomplete="off"
                        role="searchbox"
                        aria-description="search for resources"
                        id="navSearch"
                        placeholder="Search"
                        title="Search"
                        value={searchQuery}
                        onFocus={() => {
                            setShowSearchSuggestion(true);
                        }}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (
                                searchQuery.trim() !== '' &&
                                value.trim() === '' &&
                                clearSearchQuery &&
                                searchOptions.searchQuery !== ''
                            ) {
                                searchContent({ ...searchOptions, searchQuery: '' });
                            }
                            setSearchQuery(value);
                            debounce(() => getAutocompleteResults(value), 100);
                            setClearSearchQuery(false);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                searchContent({
                                    ...searchOptions,
                                    searchQuery: searchQuery.trim(),
                                });
                                setShowSearchSuggestion(false);
                            }
                        }}
                        onClick={(e) => {
                            const value = e.target.value;
                            if (value.trim() !== '') {
                                setClearSearchQuery(true);
                            } else {
                                setClearSearchQuery(false);
                            }
                        }}
                    />
                    {searchQuery.trim() && (
                        <a
                            className="im-saved-search-clear searchClose"
                            onClick={() => {
                                setSearchQuery('');
                                setClearSearchQuery(false);
                                searchContent({ ...searchOptions, searchQuery: '' }); // clear the search query
                                setShowSearchSuggestion(false);
                            }}
                        >
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M7.0005 8.05425L1.9275 13.1275C1.789 13.2658 1.61492 13.3367 1.40525 13.34C1.19575 13.3432 1.0185 13.2723 0.8735 13.1275C0.728667 12.9825 0.65625 12.8068 0.65625 12.6005C0.65625 12.3942 0.728667 12.2185 0.8735 12.0735L5.94675 7.0005L0.8735 1.9275C0.735167 1.789 0.664333 1.61492 0.661 1.40525C0.657833 1.19575 0.728667 1.0185 0.8735 0.8735C1.0185 0.728667 1.19417 0.65625 1.4005 0.65625C1.60683 0.65625 1.7825 0.728667 1.9275 0.8735L7.0005 5.94675L12.0735 0.8735C12.212 0.735167 12.3861 0.664333 12.5958 0.661C12.8053 0.657833 12.9825 0.728667 13.1275 0.8735C13.2723 1.0185 13.3448 1.19417 13.3448 1.4005C13.3448 1.60683 13.2723 1.7825 13.1275 1.9275L8.05425 7.0005L13.1275 12.0735C13.2658 12.212 13.3367 12.3861 13.34 12.5958C13.3432 12.8053 13.2723 12.9825 13.1275 13.1275C12.9825 13.2723 12.8068 13.3448 12.6005 13.3448C12.3942 13.3448 12.2185 13.2723 12.0735 13.1275L7.0005 8.05425Z"
                                    fill="#757575"
                                />
                            </svg>
                        </a>
                    )}
                    <button
                        type="button"
                        id="navSearchSubmit"
                        title="submit"
                        aria-label="Submit Search"
                        onClick={() => {
                            searchContent({ ...searchOptions, searchQuery: searchQuery.trim() });
                            setShowSearchSuggestion(false);
                        }}
                    >
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M5.125 10C3.7625 10 2.60938 9.52812 1.66563 8.58438C0.721875 7.64062 0.25 6.4875 0.25 5.125C0.25 3.7625 0.721875 2.60938 1.66563 1.66563C2.60938 0.721875 3.7625 0.25 5.125 0.25C6.4875 0.25 7.64062 0.721875 8.58438 1.66563C9.52812 2.60938 10 3.7625 10 5.125C10 5.675 9.9125 6.19375 9.7375 6.68125C9.5625 7.16875 9.325 7.6 9.025 7.975L13.225 12.175C13.3625 12.3125 13.4313 12.4875 13.4313 12.7C13.4313 12.9125 13.3625 13.0875 13.225 13.225C13.0875 13.3625 12.9125 13.4313 12.7 13.4313C12.4875 13.4313 12.3125 13.3625 12.175 13.225L7.975 9.025C7.6 9.325 7.16875 9.5625 6.68125 9.7375C6.19375 9.9125 5.675 10 5.125 10ZM5.125 8.5C6.0625 8.5 6.85938 8.17188 7.51562 7.51562C8.17188 6.85938 8.5 6.0625 8.5 5.125C8.5 4.1875 8.17188 3.39062 7.51562 2.73438C6.85938 2.07812 6.0625 1.75 5.125 1.75C4.1875 1.75 3.39062 2.07812 2.73438 2.73438C2.07812 3.39062 1.75 4.1875 1.75 5.125C1.75 6.0625 2.07812 6.85938 2.73438 7.51562C3.39062 8.17188 4.1875 8.5 5.125 8.5Z"
                                fill="white"
                            />
                        </svg>
                    </button>
                </div>
                {showSearchSuggestion && (
                    <SearchSuggestions
                        searchQuery={searchQuery}
                        setShowSearchSuggestion={setShowSearchSuggestion}
                        isPublicSearch={searchOptions.isPublicSearch}
                    />
                )}
            </div>
        </>
    );
}
